import React, { useState } from 'react';
import { Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import apiClient from '../services/apiClient';

/**
 * One-tap "Add to vocabulary" (#3) — closes the loop from journaling into the
 * spaced-repetition vocabulary (#4). Reused across the journal corrections
 * review, the entry view's glossary, and the editor.
 *
 * Props:
 *   word, translation, context  — the vocabulary fields
 *   languageCode                — target language (e.g. 'fr')
 *   size, variant, sx, fullWidth — passthrough styling (optional)
 *   maxWords                    — hide the button when `word` is longer than this
 *                                 (journal corrections can be whole phrases/sentences,
 *                                 which don't belong in vocabulary as one "word")
 *
 * A 409 (already in vocabulary) is treated as a soft success.
 */
export default function AddToVocabButton({
  word, translation, context = '', languageCode,
  size = 'small', variant = 'outlined', fullWidth = false, sx, maxWords,
}) {
  const [status, setStatus] = useState('idle'); // idle | saving | added
  const disabledByData = !word || !translation || !languageCode;
  // Word count for spaced scripts; a length cap covers scripts without spaces (ja/zh).
  const tooLong = !!maxWords && !!word &&
    (word.trim().split(/\s+/).length > maxWords || word.trim().length > 40);

  const handleAdd = async () => {
    if (disabledByData || status !== 'idle') return;
    setStatus('saving');
    try {
      await apiClient.vocabulary.addVocabularyItem({
        language_code: languageCode,
        word,
        translation,
        context: context || '',
        knowledge_level: 1,
      });
      setStatus('added');
    } catch (e) {
      if (e?.response?.status === 409) {
        setStatus('added'); // already in the list — treat as success
      } else {
        console.error('Add to vocabulary failed', e);
        setStatus('idle');
      }
    }
  };

  if (tooLong) return null;

  return (
    <Button
      size={size}
      variant={variant}
      fullWidth={fullWidth}
      disabled={disabledByData || status !== 'idle'}
      startIcon={status === 'added' ? <CheckIcon /> : <AddIcon />}
      onClick={handleAdd}
      sx={{ textTransform: 'none', ...sx }}
    >
      {status === 'added' ? 'Added' : status === 'saving' ? 'Adding…' : 'Add to vocab'}
    </Button>
  );
}
